/**
 * Moving a page's blocks (ADR 0031, amendment of 2026-10-03). Dragging, the arrow buttons and Move to top all produce
 * a new order of the same list; the editor saves the whole list in one call, as it always has. Pure, so the moves and
 * the words a screen reader hears are tested without a browser.
 */

/** The list with the item at `from` taken out and put back at `to`; unchanged for an index outside the list. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

/** Move to top: the item first, the rest in their order behind it. */
export function moveToTop<T>(items: readonly T[], index: number): T[] {
  return moveItem(items, index, 0);
}

/** What a block is called when it is announced: its type, and its headline when it has one. */
export function blockName(label: string, summary: string): string {
  return summary.trim().length > 0 ? `${label} “${summary.trim()}”` : label;
}

/** "position 1 of 6", the phrase every announcement ends with. */
export function positionPhrase(index: number, total: number): string {
  return `position ${String(index + 1)} of ${String(total)}`;
}

/**
 * The sentences a screen reader hears while a block is dragged with the keyboard or the pointer. Plain words, the
 * block named, the position counted from one.
 */
export const announce = {
  pickedUp: (name: string, index: number, total: number) =>
    `Picked up ${name}, at ${positionPhrase(index, total)}. Use the up and down arrow keys to move it, Space to drop it, Escape to cancel.`,
  over: (name: string, index: number, total: number) =>
    `${name} is over ${positionPhrase(index, total)}.`,
  moved: (name: string, index: number, total: number) =>
    `Moved ${name} to ${positionPhrase(index, total)}.`,
  droppedInPlace: (name: string, index: number, total: number) =>
    `${name} was dropped where it was, at ${positionPhrase(index, total)}.`,
  cancelled: (name: string, index: number, total: number) =>
    `Moving ${name} was cancelled. It stays at ${positionPhrase(index, total)}.`,
} as const;

/** What a keyboard user hears on focusing a drag handle, before picking it up. */
export const DRAG_INSTRUCTIONS =
  "To move this block, press Space or Enter to pick it up, the up and down arrow keys to move it, and Space or Enter again to drop it. Escape cancels. The arrow buttons and Move to top do the same.";
